import { useCallback, useState, useMemo, useEffect } from 'react'
import { useNavigate } from 'react-router'
import { debounce } from 'lodash'

import { useAppDispatch } from '@/state'
import { PlayerCardModels, PlayerCardThunks } from '@/state/player-card'
import routes from '@/routes'

function useSearchBar() {
  const [filterType, setFilterType] = useState('username')
  const [activeSearch, setActiveSearch] = useState(false)
  const [filterValue, setFilterValue] = useState('')

  const dispatch = useAppDispatch()

  const navigate = useNavigate()

  const handleChangeFilterType = (optionType: string) => {
    setFilterType(optionType)
  }

  const executeSearch = useCallback(
    (value: string, type: string) => {
      const filters = value ? { [type]: value } : {}

      dispatch(
        PlayerCardThunks.getPlayersThunk({ ...filters, limit: 25, page: 1 }),
      )

      const search = value ? `?${type}=${value}` : ''
      navigate({
        pathname: routes.playerCard.SEARCH_PLAYERS,
        search,
      })
    },
    [dispatch, navigate],
  )

  const searchDelay = useMemo(
    () =>
      debounce((value: string, type: string) => {
        executeSearch(value, type)
      }, 1000),
    [executeSearch],
  )

  useEffect(() => {
    return () => {
      searchDelay.cancel()
    }
  }, [searchDelay])

  const handleChangeSearchField = (value: string) => {
    setFilterValue(value)
    searchDelay(value, filterType)
  }

  const handleEnterSearch = () => {
    searchDelay.cancel()
    executeSearch(filterValue, filterType)
  }

  const handleSetActiveSearch = () => {
    if (activeSearch) {
      setFilterValue('')
      searchDelay.cancel()
    }
    setActiveSearch(!activeSearch)
  }

  return {
    filterType,
    filterValue,
    activeSearch,
    handleChangeSearchField,
    handleSetActiveSearch,
    handleChangeFilterType,
    handleEnterSearch,
  }
}

export default useSearchBar
